// Single source of truth for Brivio's popular services, category mapping,
// and the simple quote-first question templates shown before a request.

export const POPULAR_SERVICES = [
  { slug: "lawn-mowing", label: "Lawn mowing", category: "Lawn Care" },
  { slug: "tv-mounting", label: "TV mounting", category: "Handyman" },
  { slug: "junk-removal", label: "Junk removal", category: "Junk Removal" },
  { slug: "house-cleaning", label: "House cleaning", category: "Cleaning" },
  { slug: "moving-help", label: "Moving help", category: "Moving Help" },
  { slug: "handyman", label: "Handyman", category: "Handyman" },
  { slug: "roofing", label: "Roofing", category: "Roofing" },
  { slug: "snow-removal", label: "Snow removal", category: "Snow Removal" },
];

// Full grouped catalog powering the Get Help category picker.
// Group order here defines the section order shown in the picker.
export const SERVICE_GROUPS = [
  "Home Exterior", "Home Interior", "Cleaning",
  "Lawn & Outdoor", "Moving & Hauling", "Repairs / Trades", "Other",
];

export const SERVICE_CATALOG = [
  // Home Exterior
  { slug: "roofing", label: "Roofing", category: "Roofing", group: "Home Exterior", icon: "Home" },
  { slug: "gutters", label: "Gutters", category: "Gutters", group: "Home Exterior", icon: "Droplets" },
  { slug: "siding", label: "Siding", category: "Siding", group: "Home Exterior", icon: "Layers" },
  { slug: "windows-doors", label: "Windows & Doors", category: "Windows & Doors", group: "Home Exterior", icon: "DoorOpen" },
  { slug: "decks-patios", label: "Decks & Patios", category: "Decks & Patios", group: "Home Exterior", icon: "Grid2x2" },
  { slug: "fence-repair", label: "Fence Repair", category: "Fence Repair", group: "Home Exterior", icon: "Fence" },
  { slug: "pressure-washing", label: "Pressure Washing", category: "Pressure Washing", group: "Home Exterior", icon: "SprayCan" },
  // Home Interior
  { slug: "handyman", label: "Handyman", category: "Handyman", group: "Home Interior", icon: "Wrench" },
  { slug: "drywall-repair", label: "Drywall Repair", category: "Drywall Repair", group: "Home Interior", icon: "Hammer" },
  { slug: "painting", label: "Painting", category: "Painting", group: "Home Interior", icon: "Paintbrush" },
  { slug: "flooring", label: "Flooring", category: "Flooring", group: "Home Interior", icon: "Layers" },
  { slug: "furniture-assembly", label: "Furniture Assembly", category: "Furniture Assembly", group: "Home Interior", icon: "Sofa" },
  { slug: "tv-mounting", label: "TV Mounting", category: "Handyman", group: "Home Interior", icon: "Tv" },
  { slug: "appliance-installation", label: "Appliance Installation", category: "Appliance Installation", group: "Home Interior", icon: "Plug" },
  // Cleaning
  { slug: "house-cleaning", label: "House Cleaning", category: "Cleaning", group: "Cleaning", icon: "Sparkles" },
  { slug: "deep-cleaning", label: "Deep Cleaning", category: "Cleaning", group: "Cleaning", icon: "SprayCan" },
  { slug: "move-cleaning", label: "Move-In / Move-Out Cleaning", category: "Cleaning", group: "Cleaning", icon: "Sparkles" },
  { slug: "carpet-cleaning", label: "Carpet Cleaning", category: "Cleaning", group: "Cleaning", icon: "Sparkles" },
  { slug: "window-cleaning", label: "Window Cleaning", category: "Cleaning", group: "Cleaning", icon: "Sparkles" },
  // Lawn & Outdoor
  { slug: "lawn-mowing", label: "Lawn Mowing", category: "Lawn Care", group: "Lawn & Outdoor", icon: "Leaf" },
  { slug: "lawn-care", label: "Lawn Care", category: "Lawn Care", group: "Lawn & Outdoor", icon: "Leaf" },
  { slug: "landscaping", label: "Landscaping", category: "Landscaping", group: "Lawn & Outdoor", icon: "Trees" },
  { slug: "tree-trimming", label: "Tree Trimming", category: "Tree Trimming", group: "Lawn & Outdoor", icon: "TreePine" },
  { slug: "snow-removal", label: "Snow Removal", category: "Snow Removal", group: "Lawn & Outdoor", icon: "Snowflake" },
  { slug: "yard-cleanup", label: "Yard Cleanup", category: "Lawn Care", group: "Lawn & Outdoor", icon: "Leaf" },
  // Moving & Hauling
  { slug: "moving-help", label: "Moving Help", category: "Moving Help", group: "Moving & Hauling", icon: "Truck" },
  { slug: "junk-removal", label: "Junk Removal", category: "Junk Removal", group: "Moving & Hauling", icon: "Trash2" },
  { slug: "hauling", label: "Hauling", category: "Junk Removal", group: "Moving & Hauling", icon: "Truck" },
  { slug: "small-delivery", label: "Small Delivery", category: "Small Delivery", group: "Moving & Hauling", icon: "Package" },
  // Repairs / Trades
  { slug: "plumbing", label: "Plumbing", category: "Plumbing", group: "Repairs / Trades", icon: "Droplets" },
  { slug: "electrical", label: "Electrical", category: "Electrical", group: "Repairs / Trades", icon: "Zap" },
  { slug: "hvac", label: "HVAC", category: "HVAC", group: "Repairs / Trades", icon: "Fan" },
  { slug: "garage-door-repair", label: "Garage Door Repair", category: "Garage Door Repair", group: "Repairs / Trades", icon: "Home" },
  { slug: "appliance-repair", label: "Appliance Repair", category: "Appliance Repair", group: "Repairs / Trades", icon: "Wrench" },
  { slug: "locksmith", label: "Locksmith", category: "Locksmith", group: "Repairs / Trades", icon: "KeyRound" },
  // Other
  { slug: "general-help", label: "General Help", category: "Local help", group: "Other", icon: "HelpCircle" },
  { slug: "not-sure-other", label: "Not Sure / Other", category: "Local help", group: "Other", icon: "HelpCircle" },
];

// Shown first in the picker (before the full grouped list) so users aren't overwhelmed.
export const COMMON_SERVICE_SLUGS = [
  "lawn-mowing", "handyman", "house-cleaning", "junk-removal",
  "tv-mounting", "moving-help", "roofing", "plumbing", "electrical",
];

const bySlug = (slug) => SERVICE_CATALOG.find((x) => x.slug === slug) || null;
export const getServiceBySlug = bySlug;

// Resolve a free-text service string to a known catalog entry (or null).
export const resolveService = (service) => {
  const s = (service || "").trim().toLowerCase();
  if (!s) return null;
  return SERVICE_CATALOG.find((x) => x.label.toLowerCase() === s || x.slug === s) || null;
};

// Keyword-based detection of a service category from free-text search input.
// `high`  → confident match, auto-select silently.
// `medium`→ best guess, auto-select but tell the user they can change it.
// Order is CRITICAL: more specific / less-ambiguous categories come first so a
// phrase resolves to the intended category (e.g. "garage door" → Garage Door,
// "roof leak" → Roofing before the generic "leak" → Plumbing).
const DETECTION_RULES = [
  { slug: "garage-door-repair", high: ["garage door"], medium: ["garage"] },
  { slug: "pressure-washing", high: ["pressure wash", "power wash", "pressure-wash", "power-wash", "powerwash", "pressure washing", "power washing"], medium: [] },
  { slug: "window-cleaning", high: ["window clean", "clean window", "windows cleaned", "window washing", "wash window", "clean my windows", "windows washed"], medium: [] },
  { slug: "gutters", high: ["gutter", "downspout"], medium: [] },
  { slug: "roofing", high: ["roofing", "roof", "shingle", "reroof", "re-roof"], medium: [] },
  { slug: "siding", high: ["siding"], medium: [] },
  { slug: "decks-patios", high: ["deck", "patio"], medium: [] },
  { slug: "fence-repair", high: ["fence", "fencing"], medium: [] },
  { slug: "drywall-repair", high: ["drywall", "hole in wall", "hole in the wall", "patch wall", "wall patch", "sheetrock"], medium: [] },
  { slug: "windows-doors", high: ["window install", "install window", "replace window", "new window", "door install", "install door", "replace door", "new door", "windows and doors", "windows & doors", "screen door", "storm door", "patio door", "sliding door", "exterior door", "interior door", "hang a door", "hang door"], medium: ["window", "windows", "door", "doors"] },
  { slug: "flooring", high: ["flooring", "floor", "tile", "laminate", "hardwood", "vinyl plank", "lvp", "carpet install", "install carpet"], medium: [] },
  { slug: "carpet-cleaning", high: ["carpet"], medium: [] },
  { slug: "painting", high: ["painting", "paint", "repaint"], medium: [] },
  { slug: "furniture-assembly", high: ["assemble", "assembly", "ikea", "furniture assembly", "put together"], medium: [] },
  { slug: "tv-mounting", high: ["tv mount", "mount my tv", "mount tv", "mount television", "tv"], medium: [] },
  { slug: "appliance-installation", high: ["appliance install", "install appliance", "install dishwasher", "install washer", "install dryer", "install oven", "install microwave", "hook up appliance"], medium: [] },
  { slug: "deep-cleaning", high: ["deep clean", "deep cleaning"], medium: [] },
  { slug: "move-cleaning", high: ["move out clean", "move-out clean", "moveout clean", "move in clean", "move-in clean", "movein clean", "move out cleaning", "move in cleaning"], medium: [] },
  { slug: "house-cleaning", high: ["house clean", "clean my house", "clean the house", "cleaning", "housekeep", "maid"], medium: ["clean"] },
  { slug: "landscaping", high: ["landscap", "mulch", "bushes", "shrub", "hedge", "garden bed", "flower bed", "sod"], medium: [] },
  { slug: "tree-trimming", high: ["tree", "branch", "branches", "prune", "stump"], medium: [] },
  { slug: "lawn-mowing", high: ["mow", "cut grass", "cut the grass", "grass", "lawn"], medium: ["edging"] },
  { slug: "yard-cleanup", high: ["yard cleanup", "yard clean up", "clean up yard", "leaf removal", "rake leaves", "raking", "yard waste", "leaves", "yard clean"], medium: [] },
  { slug: "snow-removal", high: ["snow", "plow", "shovel"], medium: ["ice"] },
  { slug: "hauling", high: ["hauling", "haul away", "haul off"], medium: [] },
  { slug: "junk-removal", high: ["junk", "trash removal", "remove junk", "debris", "dumpster", "get rid of", "old couch", "old furniture", "remove old", "cleanout", "clean out garage"], medium: ["trash", "haul"] },
  { slug: "small-delivery", high: ["small delivery", "delivery", "deliver", "pickup and drop", "furniture delivery"], medium: ["pickup", "pick up"] },
  { slug: "moving-help", high: ["moving help", "help moving", "move a couch", "move furniture", "movers", "moving", "relocat", "move my"], medium: ["move"] },
  { slug: "plumbing", high: ["plumb", "sink", "toilet", "faucet", "water heater", "pipe", "clogged", "clog", "drain", "garbage disposal", "leaking pipe", "running toilet", "leak"], medium: [] },
  { slug: "electrical", high: ["outlet", "breaker", "ceiling fan", "light fixture", "light switch", "wiring", "electrical", "electric", "circuit", "gfci", "panel upgrade"], medium: [] },
  { slug: "hvac", high: ["hvac", "air condition", "a/c", "furnace", "cooling", "heating", "thermostat", "heat pump", "ac unit", "central air", "mini split", "not cooling", "not heating"], medium: [] },
  { slug: "appliance-repair", high: ["appliance repair", "appliance broken", "broken appliance", "washer", "dryer", "fridge", "refrigerator", "dishwasher", "washing machine", "oven not", "stove not", "microwave not"], medium: ["appliance"] },
  { slug: "locksmith", high: ["locksmith", "locked out", "rekey", "re-key", "change lock", "new lock", "lock repair", "lost key", "key stuck"], medium: ["lock", "key"] },
  { slug: "handyman", high: ["handyman", "shelf", "shelves", "hang picture", "mount shelf", "odd job", "small repair", "tighten", "squeaky"], medium: ["fix", "repair", "install", "hang", "mount", "patch"] },
  { slug: "not-sure-other", high: ["not sure", "something else", "dont know", "don't know"], medium: [] },
];

export const detectService = (text) => {
  const s = (text || "").toLowerCase();
  if (!s.trim()) return { service: null, confidence: "low" };
  let medium = null;
  for (const r of DETECTION_RULES) {
    if (r.high.some((k) => s.includes(k))) return { service: bySlug(r.slug), confidence: "high" };
    if (!medium && r.medium.some((k) => s.includes(k))) medium = bySlug(r.slug);
  }
  if (medium) return { service: medium, confidence: "medium" };
  return { service: null, confidence: "low" };
};

// Optional AI-fallback seam (NOT active yet — keyword detection only for beta).
// High-confidence keyword matches always short-circuit and never trigger an AI call.
// Later, pass an `aiFallback(text) -> {service, confidence}` (e.g. GPT-4o mini) to
// refine only low/medium-confidence guesses without touching the fast keyword path.
export const detectServiceSmart = async (text, { aiFallback } = {}) => {
  const result = detectService(text);
  if (result.confidence === "high" || typeof aiFallback !== "function") return result;
  try {
    const ai = await aiFallback(text);
    if (ai && ai.service) return { ...ai, source: "ai" };
  } catch { /* fall back to keyword result */ }
  return result;
};

// Turn awkward search text into a clean request description.
// "I need help with need my roof changed" → "Need my roof changed."
export const cleanDescription = (raw) => {
  let s = (raw || "").trim();
  if (!s) return "";
  s = s.replace(/^\s*(i\s+)?(need|want|would\s+like|looking\s+for)\s+(some\s+)?help\s+(with|for)\s+/i, "");
  s = s.replace(/\s+/g, " ").trim();
  s = s.charAt(0).toUpperCase() + s.slice(1);
  if (!/[.!?]$/.test(s)) s += ".";
  return s;
};

// Simple, quote-first question templates. No pricing, no estimates.
export const SERVICE_QUESTIONS = {
  "lawn-mowing": [
    { key: "area", label: "Front yard, back yard, or both?", type: "select", options: ["Front yard", "Back yard", "Both"] },
    { key: "overgrown", label: "Is the grass overgrown?", type: "bool" },
    { key: "frequency", label: "One-time or recurring?", type: "select", options: ["One-time", "Recurring"] },
    { key: "edging", label: "Any trimming/edging needed?", type: "bool" },
  ],
  "tv-mounting": [
    { key: "tv_size", label: "TV size?", type: "select", options: ['Under 40"', '40–55"', '55–65"', 'Over 65"'] },
    { key: "has_mount", label: "Do you already have a wall mount?", type: "bool" },
    { key: "wall_type", label: "Wall type, if known?", type: "select", options: ["Drywall", "Brick / Concrete", "Not sure"] },
    { key: "hide_wires", label: "Hide the wires?", type: "bool" },
  ],
  "junk-removal": [
    { key: "items", label: "What needs to be removed?", type: "text", placeholder: "e.g. old couch, boxes, yard waste" },
    { key: "amount", label: "Approximate amount?", type: "select", options: ["A few items", "About half a truck", "Full truck or more"] },
    { key: "heavy", label: "Any heavy items?", type: "bool" },
    { key: "stairs", label: "Stairs involved?", type: "bool" },
  ],
  "house-cleaning": [
    { key: "home_size", label: "Home size?", type: "select", options: ["Studio / 1BR", "2–3 bedrooms", "4+ bedrooms"] },
    { key: "clean_type", label: "Standard or deep cleaning?", type: "select", options: ["Standard", "Deep clean"] },
    { key: "bed_bath", label: "Bedrooms / bathrooms?", type: "text", placeholder: "e.g. 3 bed, 2 bath" },
    { key: "frequency", label: "One-time or recurring?", type: "select", options: ["One-time", "Recurring"] },
  ],
  "moving-help": [
    { key: "items", label: "What items need moving?", type: "text", placeholder: "e.g. 1BR apartment, appliances" },
    { key: "location_type", label: "Pickup / dropoff location type?", type: "select", options: ["House", "Apartment", "Storage unit", "Other"] },
    { key: "stairs", label: "Stairs involved?", type: "bool" },
    { key: "helpers", label: "How many helpers needed?", type: "select", options: ["1", "2", "3 or more"] },
  ],
  "handyman": [
    { key: "task", label: "What needs to be fixed or installed?", type: "text", placeholder: "e.g. hang shelves, patch drywall" },
    { key: "location", label: "Indoor or outdoor?", type: "select", options: ["Indoor", "Outdoor"] },
    { key: "materials", label: "Any parts/materials already purchased?", type: "bool" },
    { key: "urgency", label: "How soon do you need it?", type: "select", options: ["Flexible", "This week", "Urgent"] },
  ],
  "snow-removal": [
    { key: "area", label: "Driveway, walkway, or both?", type: "select", options: ["Driveway", "Walkway", "Both"] },
    { key: "size", label: "Approximate size?", type: "select", options: ["Small", "Medium", "Large"] },
    { key: "frequency", label: "One-time or recurring?", type: "select", options: ["One-time", "Recurring"] },
  ],
};

// Service-specific question keys to HIDE because the Job size / scope section
// already asks the same thing. Prevents duplicate questions in the request flow.
const SVC_QUESTION_SUPPRESS = {
  "junk-removal": ["items", "amount", "heavy", "stairs"], // fully covered by junk scope (amount, heavy, items)
  "lawn-mowing": ["area"],                                 // scope asks lawn_area
  "house-cleaning": ["home_size", "clean_type"],           // scope asks both
  "moving-help": ["stairs"],                               // scope asks move_stairs
  "snow-removal": ["area", "size"],                        // scope asks snow_area
};

// Service-specific quick questions for a service, with any questions already
// covered by the Job size / scope section removed (never ask the same thing twice).
export const serviceQuestionsFor = (svc) => {
  if (!svc) return [];
  const all = SERVICE_QUESTIONS[svc.slug] || [];
  const hide = SVC_QUESTION_SUPPRESS[svc.slug] || [];
  return hide.length ? all.filter((q) => !hide.includes(q.key)) : all;
};
export const buildRequestSummary = (svc, answers) => {
  const qs = serviceQuestionsFor(svc);
  const lines = qs
    .filter((q) => answers[q.key] !== undefined && answers[q.key] !== "" && answers[q.key] !== null)
    .map((q) => {
      let v = answers[q.key];
      if (v === true) v = "Yes";
      else if (v === false) v = "No";
      return `- ${q.label} ${v}`;
    });
  const header = svc ? `${svc.label} request:` : "Service request:";
  return lines.length ? `${header}\n${lines.join("\n")}` : header;
};

// ---- Job size / scope questions (quote-first). NOT pricing. Helps providers
// understand the job so they can send more accurate quotes. Answers are stored
// with the job and shown to matching providers — never used for price estimates.
export const SCOPE_HELP = "Not sure? That's okay. Providers can ask follow-up questions before quoting.";

export const SCOPE_QUESTIONS = {
  "lawn-mowing": [
    { key: "lawn_size", label: "About how large is the lawn?", type: "select",
      options: ["Small — under 3,000 sq ft", "Medium — 3,000–7,000 sq ft", "Large — 7,000+ sq ft", "Not sure"] },
    { key: "lawn_area", label: "Front yard, back yard, or both?", type: "select", optional: true,
      options: ["Front only", "Back only", "Front and back", "Not sure"] },
  ],
  "deck-painting": [
    { key: "deck_size", label: "About how large is the deck?", type: "select",
      options: ["Small — under 150 sq ft", "Medium — 150–350 sq ft", "Large — 350+ sq ft", "Not sure"] },
    { key: "deck_prep", label: "Does it need cleaning/sanding first?", type: "select", optional: true,
      options: ["Yes", "No", "Not sure"] },
  ],
  "deck-staining": [
    { key: "deck_size", label: "About how large is the deck?", type: "select",
      options: ["Small — under 150 sq ft", "Medium — 150–350 sq ft", "Large — 350+ sq ft", "Not sure"] },
    { key: "deck_prep", label: "Does it need cleaning/sanding first?", type: "select", optional: true,
      options: ["Yes", "No", "Not sure"] },
  ],
  "interior-painting": [
    { key: "paint_scope", label: "What needs painting?", type: "select",
      options: ["1 room", "2–3 rooms", "4+ rooms", "Whole home / large area", "Not sure"] },
    { key: "paint_sqft", label: "Approximate square footage?", type: "text", optional: true, placeholder: "e.g. 800 sq ft" },
  ],
  "flooring": [
    { key: "floor_size", label: "About how much flooring?", type: "select",
      options: ["Small — under 150 sq ft", "Medium — 150–400 sq ft", "Large — 400+ sq ft", "Not sure"] },
    { key: "floor_type", label: "Flooring type", type: "select", optional: true,
      options: ["Tile", "Vinyl plank", "Laminate", "Hardwood", "Carpet", "Not sure"] },
  ],
  "house-cleaning": [
    { key: "home_size", label: "How large is the home?", type: "select",
      options: ["Small — apartment/condo or under 1,000 sq ft", "Medium — 1,000–2,000 sq ft", "Large — 2,000+ sq ft", "Not sure"] },
    { key: "clean_type", label: "Cleaning type", type: "select", optional: true,
      options: ["Standard cleaning", "Deep cleaning", "Move-in / move-out", "Not sure"] },
  ],
  "junk-removal": [
    { key: "junk_types", label: "What types of junk? (select all that apply)", type: "multi",
      options: ["Furniture", "Appliances", "Mattresses", "Electronics", "Yard waste",
                "Construction / renovation debris", "Garage / home cleanout", "Hot tub / large single item", "Other"] },
    { key: "junk_amount", label: "How much junk needs removal?", type: "select",
      options: ["Small — a few items", "Medium — half truck/load", "Large — full truck/load or garage cleanout", "Not sure"] },
    { key: "junk_heavy", label: "Any heavy items?", type: "select", optional: true,
      options: ["Yes", "No", "Not sure"] },
    { key: "items", label: "Anything else to remove?", type: "text", optional: true,
      placeholder: "e.g. old couch, boxes, yard waste" },
  ],
  "moving-help": [
    { key: "move_amount", label: "How much needs moving?", type: "select",
      options: ["Small — 1–2 items", "Medium — one room / small move", "Large — full move / heavy items", "Not sure"] },
    { key: "move_stairs", label: "Are stairs involved?", type: "select", optional: true,
      options: ["No stairs", "One flight", "Two or more flights", "Not sure"] },
  ],
  "furniture-assembly": [
    { key: "assembly_count", label: "How many items need assembly?", type: "select",
      options: ["Small — 1 item", "Medium — 2–3 items", "Large — 4+ items", "Not sure"] },
  ],
  "snow-removal": [
    { key: "snow_area", label: "What needs clearing?", type: "select",
      options: ["Small driveway / sidewalk", "Medium driveway", "Large driveway / corner lot", "Not sure"] },
    { key: "snow_salt", label: "Need salt?", type: "select", optional: true,
      options: ["Yes", "No", "Not sure"] },
  ],
  "handyman": [
    { key: "project_size", label: "How big is the project?", type: "select",
      options: ["Small — one simple task", "Medium — a few tasks / half day", "Large — many tasks / full day+", "Not sure"] },
  ],
  "roofing": [
    { key: "roof_work", label: "What kind of roofing work?", type: "select",
      options: ["Repair / leak", "Full replacement", "Inspection", "Gutters", "Not sure"] },
    { key: "roof_stories", label: "How many stories is the building?", type: "select", optional: true,
      options: ["1 story", "2 stories", "3+ stories", "Not sure"] },
  ],
};

export const GENERIC_SCOPE = [
  { key: "job_size", label: "How big is the job?", type: "select", options: ["Small", "Medium", "Large", "Not sure"] },
];

// Resolve the scope questions for a chosen service (by slug, then category, then generic).
export const scopeQuestionsFor = (svc) => {
  if (!svc) return [];
  if (SCOPE_QUESTIONS[svc.slug]) return SCOPE_QUESTIONS[svc.slug];
  const c = (svc.category || "").toLowerCase();
  const l = (svc.label || "").toLowerCase();
  const hay = `${c} ${l}`;
  if (/roof|shingle|gutter/.test(hay)) return SCOPE_QUESTIONS["roofing"];
  if (/lawn|yard|mow/.test(hay)) return SCOPE_QUESTIONS["lawn-mowing"];
  if (/deck/.test(hay) && /stain/.test(hay)) return SCOPE_QUESTIONS["deck-staining"];
  if (/deck/.test(hay)) return SCOPE_QUESTIONS["deck-painting"];
  if (/paint/.test(hay)) return SCOPE_QUESTIONS["interior-painting"];
  if (/floor/.test(hay)) return SCOPE_QUESTIONS["flooring"];
  if (/clean/.test(hay)) return SCOPE_QUESTIONS["house-cleaning"];
  if (/junk|haul/.test(hay)) return SCOPE_QUESTIONS["junk-removal"];
  if (/mov/.test(hay)) return SCOPE_QUESTIONS["moving-help"];
  if (/assembl|furnitur/.test(hay)) return SCOPE_QUESTIONS["furniture-assembly"];
  if (/snow/.test(hay)) return SCOPE_QUESTIONS["snow-removal"];
  if (/handy/.test(hay)) return SCOPE_QUESTIONS["handyman"];
  return GENERIC_SCOPE;
};

// Build a structured, provider-facing scope list from answers: [{label, value}].
// Multi-select answers (arrays) are joined into a comma-separated string.
export const buildScope = (svc, answers) =>
  scopeQuestionsFor(svc)
    .filter((q) => {
      const v = answers[q.key];
      if (v === undefined || v === null || v === "") return false;
      if (Array.isArray(v)) return v.length > 0;
      return true;
    })
    .map((q) => {
      const v = answers[q.key];
      return { key: q.key, label: q.label, value: Array.isArray(v) ? v.join(", ") : String(v) };
    });

// Junk Removal provider capabilities (Stage C). Descriptive only — never used for pricing.
export const JUNK_CAPABILITIES = [
  "Full-service (we load & haul)",
  "Dump-trailer / bin rental",
  "Both",
];

// =====================================================================
// UNIVERSAL REQUEST-FLOW CONFIG (config-driven Step 2 "Job details").
// Keyed by service SLUG (falls back to category, then default). Each entry:
//   placeholder, questions[] (<=3, single|multi), schedule mode, ack,
//   optional scheduleRecurringWhen (switch to recurring scheduling), and
//   per-question `suggest` (high-confidence description-aware preselects).
// No pricing/weight/volume/labor/technical-diagnosis fields. "Not sure"
// offered wherever the customer may not know the answer.
// =====================================================================

export const DEFAULT_PLACEHOLDER =
  "Example: Describe the job, location of the work, and anything the provider should know.";

export const REQUEST_PLACEHOLDERS = {
  "junk-removal": "Example: One queen mattress and an old dresser in the basement. There is one flight of stairs.",
  "hauling": "Example: A pile of construction debris and an old couch in the driveway.",
  "small-delivery": "Example: Pick up a dresser from a store across town and deliver it to my apartment.",
  "lawn-mowing": "Example: I need the front and back lawn mowed and the edges trimmed.",
  "lawn-care": "Example: I'd like fertilization and weed control for my front and back yard.",
  "landscaping": "Example: I want new mulch and a few shrubs planted along the front walkway.",
  "yard-cleanup": "Example: Leaves and branches need to be cleared from the back yard.",
  "tree-trimming": "Example: A large branch is hanging over the driveway and needs trimming.",
  "snow-removal": "Example: I need my driveway and front sidewalk cleared after each snowfall.",
  "house-cleaning": "Example: I need a standard cleaning for a three-bedroom, two-bathroom home.",
  "deep-cleaning": "Example: I'd like a deep cleaning of my two-bedroom apartment.",
  "move-cleaning": "Example: Move-out cleaning for a two-bedroom, one-bathroom apartment.",
  "carpet-cleaning": "Example: Carpet cleaning for three rooms and a staircase; a few pet stains.",
  "window-cleaning": "Example: Interior and exterior window cleaning for a two-story home.",
  "plumbing": "Example: The kitchen sink is leaking underneath the cabinet.",
  "electrical": "Example: An outlet in the kitchen stopped working and the switch feels warm.",
  "hvac": "Example: The AC is running but the house isn't cooling.",
  "garage-door-repair": "Example: The garage door won't open and makes a grinding noise.",
  "appliance-repair": "Example: The dishwasher won't drain and leaves standing water.",
  "locksmith": "Example: I'm locked out of my house and need to get back in.",
  "moving-help": "Example: I need help loading furniture from a second-floor apartment into a rental truck.",
  "handyman": "Example: I need two shelves installed and a damaged cabinet hinge repaired.",
  "drywall-repair": "Example: There's a hole in the living-room wall about the size of a doorknob.",
  "painting": "Example: I'd like the living room and hallway repainted.",
  "flooring": "Example: I need laminate flooring installed in two bedrooms.",
  "furniture-assembly": "Example: I have a bed frame and a bookshelf that need assembling.",
  "tv-mounting": "Example: Mount a 55-inch TV on a drywall living-room wall.",
  "appliance-installation": "Example: Install a new dishwasher and remove the old one.",
  "roofing": "Example: A few shingles blew off and there's a small leak near the chimney.",
  "gutters": "Example: The gutters are clogged and one downspout is pulling away from the house.",
  "siding": "Example: A section of vinyl siding cracked and needs replacing.",
  "windows-doors": "Example: I need one exterior door replaced and two windows repaired.",
  "decks-patios": "Example: A few deck boards are rotting and need to be replaced.",
  "fence-repair": "Example: Two fence panels fell over and the gate won't latch.",
  "pressure-washing": "Example: I'd like the driveway and back patio pressure washed.",
};

const YES_NO_NS = ["Yes", "No", "Not sure"];
const YARD_SIZE = ["Small", "Medium", "Large", "Not sure"];
const HOME_SIZE = ["Studio or one bedroom", "Two to three bedrooms", "Four or more bedrooms", "Not sure"];
const BATHROOMS = ["One", "Two", "Three or more", "Not sure"];
const STORIES = ["One", "Two", "Three or more", "Not sure"];

// Reusable high-confidence timing suggestion (applied to the schedule control).
export const TIMING_SUGGEST = [
  [/\bemergency\b|right now|immediately/i, "emergency"],
  [/\basap\b|as soon as possible|right away|urgent/i, "asap"],
  [/within a week|within 7 days|this week/i, "week"],
];

const SERVICE_REQUEST_CONFIG = {
  // ---- Lawn & Outdoor ----
  "lawn-mowing": {
    schedule: "standard", scheduleRecurringWhen: { key: "lawn_recurring", value: "Recurring service" },
    questions: [
      { key: "lawn_service", label: "What service do you need?", type: "single",
        options: ["Mowing and edging", "Mowing only", "Yard cleanup", "Trimming", "Other / not sure"] },
      { key: "lawn_recurring", label: "Is this a one-time or recurring service?", type: "single",
        options: ["One-time service", "Recurring service", "Not sure"],
        suggest: [[/recurring|every week|weekly|bi-?weekly|all season|each week/i, "Recurring service"], [/one[- ]?time|just once|single/i, "One-time service"]] },
      { key: "lawn_size", label: "Approximate yard size", type: "single", options: YARD_SIZE },
    ],
  },
  "lawn-care": {
    schedule: "standard", scheduleRecurringWhen: { key: "lawn_recurring", value: "Recurring" },
    questions: [
      { key: "lawncare_type", label: "What type of lawn care?", type: "single",
        options: ["Fertilization", "Weed control", "Aeration or overseeding", "General lawn treatment", "Other / not sure"] },
      { key: "lawn_recurring", label: "Is this a one-time or recurring service?", type: "single",
        options: ["One-time", "Recurring", "Not sure"],
        suggest: [[/recurring|every|weekly|monthly|season/i, "Recurring"], [/one[- ]?time|once/i, "One-time"]] },
      { key: "lawn_size", label: "Approximate yard size", type: "single", options: YARD_SIZE },
    ],
  },
  "landscaping": {
    schedule: "standard", scheduleRecurringWhen: { key: "land_recurring", value: "Ongoing" },
    questions: [
      { key: "land_type", label: "What type of landscaping help?", type: "single",
        options: ["New landscaping", "Planting", "Mulch or rock", "Landscape maintenance", "Other / not sure"] },
      { key: "land_recurring", label: "Is this a one-time or ongoing service?", type: "single", options: ["One-time", "Ongoing", "Not sure"] },
    ],
  },
  "tree-trimming": {
    schedule: "standard", ack: "treesafety",
    questions: [
      { key: "tree_service", label: "What service do you need?", type: "single",
        options: ["Tree trimming", "Tree removal", "Stump removal", "Other / not sure"] },
      { key: "tree_near", label: "Is the tree near power lines or a structure?", type: "single", options: YES_NO_NS },
    ],
  },
  "yard-cleanup": {
    schedule: "standard", scheduleRecurringWhen: { key: "yc_recurring", value: "Recurring" },
    questions: [
      { key: "yc_what", label: "What needs cleanup? Select all that apply.", type: "multi",
        options: ["Leaves", "Branches", "Weeds or overgrowth", "General debris", "Other / not sure"] },
      { key: "yc_recurring", label: "Is this a one-time or recurring service?", type: "single", options: ["One-time", "Recurring", "Not sure"] },
    ],
  },
  "snow-removal": {
    schedule: "snow", scheduleRecurringWhen: { key: "snow_recurring", value: "Recurring service" },
    questions: [
      { key: "snow_area", label: "What needs clearing? Select all that apply.", type: "multi",
        options: ["Driveway", "Sidewalk", "Parking area", "Steps or entryway", "Other / not sure"] },
      { key: "snow_recurring", label: "Is this a one-time or recurring service?", type: "single",
        options: ["One-time service", "Recurring service", "Not sure"],
        suggest: [[/recurring|all winter|each snow|every snow|season/i, "Recurring service"]] },
    ],
  },

  // ---- Cleaning ----
  "house-cleaning": {
    schedule: "appointment",
    questions: [
      { key: "clean_type", label: "What type of cleaning?", type: "single",
        options: ["Standard cleaning", "Deep cleaning", "Move-in or move-out", "Not sure"],
        suggest: [[/deep clean/i, "Deep cleaning"], [/move[- ]?(in|out)/i, "Move-in or move-out"], [/standard|regular|basic clean/i, "Standard cleaning"]] },
      { key: "home_size", label: "Home size", type: "single", options: HOME_SIZE,
        suggest: [[/studio|one bed|1 bed|1-bed/i, "Studio or one bedroom"], [/(two|three|2|3)[- ]?bed/i, "Two to three bedrooms"], [/(four|five|4|5|6)[- ]?bed/i, "Four or more bedrooms"]] },
      { key: "bathrooms", label: "Number of bathrooms", type: "single", options: BATHROOMS,
        suggest: [[/\b1 bath|one bath\b/i, "One"], [/\b2 bath|two bath\b/i, "Two"], [/\b(3|three|4|four)[- ]?bath/i, "Three or more"]] },
    ],
  },
  "deep-cleaning": {
    schedule: "appointment",
    questions: [
      { key: "home_size", label: "Home size", type: "single", options: HOME_SIZE },
      { key: "bathrooms", label: "Number of bathrooms", type: "single", options: BATHROOMS },
    ],
  },
  "move-cleaning": {
    schedule: "appointment",
    questions: [
      { key: "move_clean_type", label: "Is this a move-in or move-out cleaning?", type: "single", options: ["Move-in cleaning", "Move-out cleaning", "Not sure"] },
      { key: "home_size", label: "Home size", type: "single", options: HOME_SIZE },
      { key: "bathrooms", label: "Number of bathrooms", type: "single", options: BATHROOMS },
    ],
  },
  "carpet-cleaning": {
    schedule: "appointment",
    questions: [
      { key: "carpet_rooms", label: "How many carpeted rooms or areas?", type: "single", options: ["One", "Two to three", "Four or more", "Whole home", "Not sure"] },
      { key: "carpet_stairs", label: "Are there carpeted stairs?", type: "single", options: YES_NO_NS },
      { key: "carpet_stains", label: "Any major stains or pet odors?", type: "single", options: YES_NO_NS },
    ],
  },
  "window-cleaning": {
    schedule: "appointment",
    questions: [
      { key: "wc_what", label: "What needs cleaning?", type: "single", options: ["Interior windows", "Exterior windows", "Both", "Not sure"] },
      { key: "wc_count", label: "Approximate number of windows", type: "single", options: ["1–10", "11–20", "More than 20", "Not sure"] },
      { key: "wc_stories", label: "How many stories?", type: "single", options: STORIES },
    ],
  },

  // ---- Moving & Hauling ----
  "moving-help": {
    schedule: "moving",
    questions: [
      { key: "move_help", label: "What help do you need?", type: "single", options: ["Loading", "Unloading", "Both", "Moving items within the property"] },
      { key: "move_stairs", label: "Are there stairs?", type: "single", options: YES_NO_NS,
        suggest: [[/stairs|flight of stairs|second floor|2nd floor|upstairs|third floor/i, "Yes"]] },
      { key: "move_truck", label: "Will you provide the moving truck?", type: "single", options: YES_NO_NS },
    ],
  },
  "junk-removal": {
    schedule: "standard", ack: "junk",
    questions: [
      { key: "junk_access", label: "Where are the items?", type: "single",
        options: ["Curb, driveway, or garage", "Inside the home", "Basement or upstairs", "Other / not sure"] },
    ],
  },
  "hauling": {
    schedule: "standard",
    questions: [
      { key: "haul_what", label: "What needs hauling? Select all that apply.", type: "multi", options: ["Furniture", "Construction debris", "Yard debris", "General items", "Other / not sure"] },
      { key: "haul_access", label: "Where are the items?", type: "single", options: ["Curb, driveway, or garage", "Inside the property", "Basement or upstairs", "Other / not sure"] },
    ],
  },
  "small-delivery": {
    schedule: "standard",
    questions: [
      { key: "deliver_what", label: "What is being delivered?", type: "single", options: ["Furniture", "Appliance", "Boxes or household items", "Other / not sure"] },
      { key: "deliver_help", label: "Will loading or unloading help be needed?", type: "single", options: ["Loading", "Unloading", "Both", "No help needed", "Not sure"] },
    ],
  },

  // ---- Repairs / Trades ----
  "plumbing": {
    schedule: "urgent", ack: "plumbing",
    questions: [
      { key: "plumb_issue", label: "What type of issue?", type: "single",
        options: ["Leak", "Clogged drain or toilet", "Fixture installation or repair", "Water heater", "Other / not sure"],
        suggest: [[/toilet.*(back|clog)|clogged|clog|backed up|drain/i, "Clogged drain or toilet"], [/leak|leaking|drip/i, "Leak"], [/water heater/i, "Water heater"], [/faucet|fixture|install/i, "Fixture installation or repair"]] },
      { key: "plumb_leaking", label: "Is water actively leaking?", type: "single", options: YES_NO_NS,
        suggest: [[/actively leaking|water everywhere|flooding|gushing/i, "Yes"]] },
    ],
  },
  "electrical": {
    schedule: "urgent", ack: "electrical",
    questions: [
      { key: "elec_work", label: "What type of electrical work?", type: "single",
        options: ["Outlet or switch", "Lighting or fixture", "Panel or power issue", "New wiring or whole-home rewiring", "Other / not sure"],
        suggest: [[/whole.?home|whole house|rewir|new wiring/i, "New wiring or whole-home rewiring"], [/outlet|switch/i, "Outlet or switch"], [/light|fixture|fan/i, "Lighting or fixture"], [/panel|breaker|no power|power issue|circuit/i, "Panel or power issue"]] },
      { key: "elec_safety", label: "Is there an immediate safety concern?", type: "single", options: YES_NO_NS,
        showIf: (a, desc) => a.elec_work === "Panel or power issue" || /spark|smoke|burning|burn smell|exposed wire|live wire|breaker trip|electrical damage|shock/i.test(desc || "") },
    ],
  },
  "hvac": {
    schedule: "urgent",
    questions: [
      { key: "hvac_help", label: "What HVAC help do you need?", type: "single", options: ["Heating", "Cooling", "Maintenance", "New installation", "Other / not sure"] },
      { key: "hvac_working", label: "Is the system currently working?", type: "single", options: ["Yes", "No", "Partially", "Not sure"] },
    ],
  },
  "garage-door-repair": {
    schedule: "urgent",
    questions: [
      { key: "garage_issue", label: "What is the issue?", type: "single", options: ["Door will not open or close", "Spring or cable", "Opener or remote", "New door or opener installation", "Other / not sure"] },
      { key: "garage_trapped", label: "Is a vehicle trapped inside?", type: "single", options: YES_NO_NS },
    ],
  },
  "appliance-repair": {
    schedule: "standard",
    questions: [
      { key: "appl_which", label: "Which appliance needs repair?", type: "single", options: ["Refrigerator", "Dishwasher", "Washer or dryer", "Oven or range", "Other / not sure"] },
      { key: "appl_unusable", label: "Is the appliance completely unusable?", type: "single", options: YES_NO_NS },
    ],
  },
  "locksmith": {
    schedule: "urgent",
    questions: [
      { key: "lock_service", label: "What locksmith service do you need?", type: "single",
        options: ["Locked out", "Rekey or change locks", "Repair a lock", "Install new locks", "Other / not sure"],
        suggest: [[/locked out|lock ?out|can'?t get in/i, "Locked out"], [/rekey|re-key|change lock/i, "Rekey or change locks"]] },
    ],
  },

  // ---- Home Interior ----
  "handyman": {
    schedule: "standard",
    questions: [
      { key: "handy_work", label: "What type of work?", type: "single", options: ["Assembly", "Installation or mounting", "Minor repair", "Multiple small jobs", "Other / not sure"] },
      { key: "handy_location", label: "Is the work indoors or outdoors?", type: "single", options: ["Indoors", "Outdoors", "Both", "Not sure"] },
    ],
  },
  "drywall-repair": {
    schedule: "standard",
    questions: [
      { key: "drywall_type", label: "What type of drywall work?", type: "single", options: ["Small patch", "Wall repair", "Ceiling repair", "Water-damage repair", "New drywall", "Other / not sure"] },
      { key: "drywall_area", label: "What area is affected?", type: "single", options: ["One small area", "One wall or ceiling area", "Multiple areas", "Not sure"] },
    ],
  },
  "painting": {
    schedule: "standard",
    questions: [
      { key: "paint_what", label: "What needs painting?", type: "single", options: ["Interior", "Exterior", "Cabinets or furniture", "Other / not sure"],
        suggest: [[/interior|inside|bedroom|living room|hallway|room/i, "Interior"], [/exterior|outside|outdoor/i, "Exterior"], [/cabinet|furniture/i, "Cabinets or furniture"]] },
      { key: "paint_materials", label: "Are materials already purchased?", type: "single", options: YES_NO_NS },
    ],
  },
  "flooring": {
    schedule: "standard",
    questions: [
      { key: "floor_service", label: "What flooring service do you need?", type: "single", options: ["Repair", "Replacement", "New installation", "Other / not sure"] },
      { key: "floor_type", label: "What type of flooring?", type: "single", options: ["Hardwood", "Laminate", "Vinyl", "Tile", "Carpet", "Not sure"] },
      { key: "floor_materials", label: "Are materials already purchased?", type: "single", options: YES_NO_NS },
    ],
  },
  "furniture-assembly": {
    schedule: "standard",
    questions: [
      { key: "fa_help", label: "What help do you need?", type: "single", options: ["Assembly", "Disassembly", "Both", "Other / not sure"] },
      { key: "fa_onsite", label: "Are the items already at the property?", type: "single", options: YES_NO_NS },
    ],
  },
  "tv-mounting": {
    schedule: "standard",
    questions: [
      { key: "tv_mount", label: "Do you already have the wall mount?", type: "single", options: YES_NO_NS },
      { key: "tv_wall", label: "What type of mounting surface?", type: "single", options: ["Drywall", "Brick or concrete", "Exterior siding", "Other / not sure"] },
    ],
  },
  "appliance-installation": {
    schedule: "standard",
    questions: [
      { key: "appli_what", label: "What appliance?", type: "single", options: ["Refrigerator", "Dishwasher", "Washer or dryer", "Oven or range", "Other / not sure"] },
      { key: "appli_hookups", label: "Are the required hookups already available?", type: "single", options: YES_NO_NS },
      { key: "appli_removal", label: "Does the old appliance need removal?", type: "single", options: YES_NO_NS },
    ],
  },

  // ---- Home Exterior ----
  "roofing": {
    schedule: "standard", ack: "roofing",
    questions: [
      { key: "roof_service", label: "What roofing service do you need?", type: "single",
        options: ["Inspection", "Repair", "Active leak", "Replacement", "New installation", "Other / not sure"],
        suggest: [[/leak/i, "Active leak"], [/replace|reroof|re-roof|new roof|tear off/i, "Replacement"], [/inspect/i, "Inspection"]] },
      { key: "roof_leaking", label: "Is the roof actively leaking?", type: "single", options: YES_NO_NS,
        suggest: [[/leak/i, "Yes"]] },
      { key: "roof_stories", label: "How many stories is the property?", type: "single", options: STORIES },
    ],
  },
  "gutters": {
    schedule: "standard",
    questions: [
      { key: "gutter_service", label: "What gutter service do you need?", type: "single", options: ["Cleaning", "Repair", "Replacement", "New installation", "Other / not sure"],
        suggest: [[/replace|replacement|new gutter/i, "Replacement"], [/clean/i, "Cleaning"], [/repair|fix/i, "Repair"]] },
      { key: "gutter_what", label: "What needs service?", type: "single", options: ["Gutters", "Downspouts", "Both", "Not sure"] },
      { key: "gutter_stories", label: "How many stories is the property?", type: "single", options: STORIES },
    ],
  },
  "siding": {
    schedule: "standard",
    questions: [
      { key: "siding_service", label: "What siding service do you need?", type: "single", options: ["Repair", "Replacement", "New installation", "Other / not sure"] },
      { key: "siding_material", label: "What siding material?", type: "single", options: ["Vinyl", "Wood", "Fiber cement", "Other", "Not sure"] },
      { key: "siding_stories", label: "How many stories is the property?", type: "single", options: STORIES },
    ],
  },
  "windows-doors": {
    schedule: "standard",
    questions: [
      { key: "wd_service", label: "What service do you need?", type: "single", options: ["Repair", "Replacement", "New installation", "Not sure"] },
      { key: "wd_what", label: "What is involved?", type: "single", options: ["Windows", "Doors", "Both", "Not sure"] },
      { key: "wd_materials", label: "Are materials already purchased?", type: "single", options: YES_NO_NS },
    ],
  },
  "decks-patios": {
    schedule: "standard",
    questions: [
      { key: "deck_project", label: "What type of project?", type: "single", options: ["Repair or replace boards", "Rebuild or replace", "New deck or patio", "Removal only", "Other / not sure"] },
      { key: "deck_type", label: "Is this for a deck or patio?", type: "single", options: ["Deck", "Patio", "Both", "Not sure"] },
    ],
  },
  "fence-repair": {
    schedule: "standard",
    questions: [
      { key: "fence_service", label: "What service do you need?", type: "single", options: ["Repair", "Replacement", "New installation", "Other / not sure"] },
      { key: "fence_type", label: "What type of fence?", type: "single", options: ["Wood", "Vinyl", "Chain-link", "Metal", "Other / not sure"] },
      { key: "fence_gate", label: "Is a gate included?", type: "single", options: YES_NO_NS },
    ],
  },
  "pressure-washing": {
    schedule: "standard",
    questions: [
      { key: "pw_what", label: "What needs pressure washing? Select all that apply.", type: "multi", options: ["Driveway or walkway", "Siding", "Deck or patio", "Fence", "Other / not sure"] },
    ],
  },
};

// General Help / Not Sure / Other and any unmapped service: description + photos
// + location + standard scheduling. NO generic size question.
export const DEFAULT_REQUEST_CONFIG = { questions: [], schedule: "standard", ack: null };

// Resolve the universal request config for a chosen service (by slug → category → default).
export const requestConfigFor = (svc) => {
  const base = (svc?.slug && SERVICE_REQUEST_CONFIG[svc.slug]) || DEFAULT_REQUEST_CONFIG;
  const placeholder = (svc?.slug && REQUEST_PLACEHOLDERS[svc.slug]) || DEFAULT_PLACEHOLDER;
  return {
    questions: base.questions || [],
    schedule: base.schedule || "standard",
    scheduleRecurringWhen: base.scheduleRecurringWhen || null,
    ack: base.ack || null,
    placeholder,
  };
};

// Map a schedule value to the backend urgency field (today | flexible). No pricing.
export const urgencyForTiming = (k) => (["asap", "emergency"].includes(k) ? "today" : "flexible");
